import React, { useCallback, useEffect, useState } from 'react';

import { brands } from '../../../consts/brands';
import OffersService from '../../../services/OffersService';
import { reportError } from '../../../utils/reportError';
import shouldMigrateScheduleData from '../../../utils/shouldMigrateScheduleData';

import NewSchedulePageEdit from './NewSchedulePageEdit';

type Props = {
  match: {
    params: {
      id_offer: string;
    };
  };
};

export default function NewScheduleContainer({ match }: Props) {
  const { id_offer: offerId } = match.params;

  const [schedules, setSchedules] = useState<App.Schedule[]>([]);
  const [isFetched, setIsFetched] = useState(false);
  const [isMigratingData, setIsMigratingData] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [offerName, setOfferName] = useState<string | undefined>();
  const [shouldMigrateSchedule, setShouldMigrateSchedule] = useState<boolean | undefined>();
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const brandValueList = brands.map((brand) => brand.value);
    OffersService.getOffer(offerId)
      .then((response) => {
        setShouldMigrateSchedule(shouldMigrateScheduleData(response.result.brand_schedules, brandValueList));
        setOfferName(response.result.name);
        setSchedules(response.result.brand_schedules);
        setIsFetched(true);
      })
      .catch((e) => {
        reportError(e);
        setMigrationError(e.message ?? 'Failed to load offer');
        setIsFetched(true);
      });
  }, [offerId]);

  const migrateScheduleData = useCallback(async () => {
    setIsMigratingData(true);
    setMigrationError(null);

    const brandValueList = brands.map((brand) => brand.value);
    let error: string | undefined;
    try {
      await OffersService.migrateScheduleData({ brands: brandValueList }, offerId);
    } catch (e) {
      error = e.name;
    }

    const response = await OffersService.getOffer(offerId);
    const newShouldMigrate = shouldMigrateScheduleData(response.result.brand_schedules, brandValueList);

    if (!newShouldMigrate || !error) {
      error = undefined;
    }

    setShouldMigrateSchedule(newShouldMigrate);
    setSchedules(response.result.brand_schedules);
    setIsMigratingData(false);
    setMigrationError(error ?? null);
    setRefreshToken((prev) => prev + 1);
  }, [offerId]);

  if (!isFetched) return null;

  return (
    <NewSchedulePageEdit
      offerName={offerName}
      schedules={schedules}
      offerId={offerId}
      shouldMigrateSchedule={shouldMigrateSchedule}
      migrateScheduleData={migrateScheduleData}
      isMigratingData={isMigratingData}
      migrationError={migrationError}
      refreshToken={refreshToken}
    />
  );
}
